import { Form, Input, Modal } from 'antd';
import { ReactNode, useState } from 'react';
import CustomButton from 'components/custom-button';

export interface NoteActionConfig {
  title: string;
  description?: ReactNode;
  noteLabel?: string;
  noteRequired?: boolean;
  confirmText: string;
  danger?: boolean;
  /** Thêm ô "Mã nhận hàng" (giao đơn Shop): mã được truyền vào onSubmit thứ hai. */
  codeField?: { label: string; help?: ReactNode };
  // Gọi API; ném lỗi thì modal giữ nguyên để người dùng thử lại.
  onSubmit: (note?: string, code?: string) => Promise<void>;
}

interface Props {
  config: NoteActionConfig | null;
  onClose: () => void;
}

// Modal chung "xác nhận + ghi chú" cho duyệt / từ chối / giao / huỷ (đơn lẻ hoặc hàng loạt).
const NoteActionModal = ({ config, onClose }: Props) => {
  const [form] = Form.useForm<{ note?: string; code?: string }>();
  const [loading, setLoading] = useState(false);
  const open = !!config;

  const handleClose = () => {
    if (loading) return;
    form.resetFields();
    onClose();
  };

  const onFinish = async (values: { note?: string; code?: string }) => {
    if (!config) return;
    setLoading(true);
    try {
      await config.onSubmit(
        values.note?.trim() || undefined,
        values.code?.trim().toUpperCase() || undefined
      );
      form.resetFields();
      onClose();
    } catch {
      // Người gọi đã báo lỗi; giữ modal mở.
    } finally {
      setLoading(false);
    }
  };

  const required = !!config?.noteRequired;

  return (
    <Modal
      title={config?.title}
      open={open}
      onCancel={handleClose}
      footer={null}
      destroyOnClose
      maskClosable={false}
      width={520}
    >
      {config?.description && <div style={{ marginBottom: 16 }}>{config.description}</div>}
      <Form form={form} layout="vertical" onFinish={onFinish} className="custom-form">
        {config?.codeField && (
          <Form.Item
            label={config.codeField.label}
            name="code"
            extra={config.codeField.help}
            normalize={(value?: string) => value?.replace(/\s+/g, '').toUpperCase()}
            rules={[
              {
                pattern: /^[A-Za-z2-9]{6}$/,
                message: 'Mã nhận hàng gồm 6 ký tự.',
              },
            ]}
          >
            <Input
              maxLength={6}
              autoComplete="off"
              placeholder="VD: A7K2QX"
              style={{ fontFamily: 'monospace', fontSize: 18, letterSpacing: 3 }}
            />
          </Form.Item>
        )}
        <Form.Item
          label={config?.noteLabel || (required ? 'Lý do' : 'Ghi chú (không bắt buộc)')}
          name="note"
          rules={[
            { max: 255, message: 'Ghi chú tối đa 255 ký tự.' },
            {
              validator: (_, value) =>
                !required || value?.trim()
                  ? Promise.resolve()
                  : Promise.reject(new Error('Vui lòng nhập lý do.')),
            },
          ]}
        >
          <Input.TextArea rows={3} maxLength={255} showCount placeholder="Nhập nội dung" />
        </Form.Item>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <CustomButton text="Huỷ" bgColor="primary-grey" onClick={handleClose} />
          <CustomButton
            text={config?.confirmText}
            bgColor={config?.danger ? 'primary-red' : 'secondary-green'}
            htmlType="submit"
            loading={loading}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default NoteActionModal;
